import React, { memo, useCallback, useMemo } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { ModelPicker, Tooltip, BiX } from '@acme/ui';
import {
    allModelOptions,
    DEFAULT_MODEL,
    DEFAULT_MODEL_BY_PROVIDER,
    getModelProvider,
    type ChatModel,
} from '@acme/util';
import { compareModelAtom } from '../store/compare';
import { chatSessionActiveInfoAtom, configuredProvidersAtom } from '../store/chat';

/** Two overlapping panes; reads as "side by side" at toolbar size. */
const CompareIcon = () => (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <rect x="1.5" y="2.5" width="6" height="11" rx="1.5" />
        <rect x="8.5" y="2.5" width="6" height="11" rx="1.5" />
    </svg>
);

/**
 * Composer toggle for compare mode: send each message to a second model and
 * see both replies side by side. Prefers a model from a different provider the
 * user already has a key for, since that's the comparison people want.
 */
export const CompareToggle = memo(() => {
    const [compareModel, setCompareModel] = useAtom(compareModelAtom);
    const session = useAtomValue(chatSessionActiveInfoAtom);
    const configured = useAtomValue(configuredProvidersAtom);

    const chatModel: ChatModel = session?.defaultModel || DEFAULT_MODEL;
    const isSynced = !!session?.isSync;

    const pickDefault = useCallback((): ChatModel | null => {
        const chatProvider = getModelProvider(chatModel);
        const otherProvider = configured.find(p => p !== chatProvider);
        if (otherProvider) return DEFAULT_MODEL_BY_PROVIDER[otherProvider];
        // Only one provider configured: compare within it.
        return allModelOptions.find(m => m.type === chatProvider && m.value !== chatModel)?.value ?? null;
    }, [chatModel, configured]);

    const options = useMemo(() => allModelOptions.filter(m => m.value !== chatModel), [chatModel]);

    if (isSynced) {
        return (
            <Tooltip message="Compare works in local chats for now" position="bottom">
                <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-100 dark:bg-neutral-700 text-gray-400 dark:text-gray-500 cursor-not-allowed">
                    <CompareIcon /> Compare
                </span>
            </Tooltip>
        );
    }

    if (!compareModel) {
        return (
            <Tooltip message="Ask a second model and see both answers side by side" position="bottom">
                <button
                    type="button"
                    onClick={() => setCompareModel(pickDefault())}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-neutral-600"
                >
                    <CompareIcon /> Compare
                </button>
            </Tooltip>
        );
    }

    return (
        <div className="flex items-center gap-1 rounded-lg bg-amber-500/15 pl-2.5 pr-1 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300">
            <CompareIcon />
            <span className="whitespace-nowrap">vs</span>
            <ModelPicker
                size="sm"
                placement="top"
                value={compareModel}
                options={options}
                onChange={setCompareModel}
            />
            <Tooltip message="Stop comparing" position="bottom">
                <button
                    type="button"
                    aria-label="Stop comparing"
                    onClick={() => setCompareModel(null)}
                    className="p-1 rounded hover:bg-amber-500/20"
                >
                    <BiX className="text-sm" />
                </button>
            </Tooltip>
        </div>
    );
});
CompareToggle.displayName = 'CompareToggle';
